import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SystemReset = ({ user }) => {
  const navigate = useNavigate();
  const [confirmationText, setConfirmationText] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Protect route - only superadmin can access
  useEffect(() => {
    if (!user || user.role !== 'superadmin') {
      toast.error('Acceso denegado: Solo Superadmin puede acceder a esta página');
      navigate('/');
    }
  }, [user, navigate]);

  const isFormValid = confirmationText === 'CONFIRMAR RESET' && password.length > 0;

  const handleReset = async () => {
    if (!isFormValid) {
      toast.error('Completa todos los campos correctamente');
      return;
    }

    setLoading(true);
    setResetResult(null);

    try {
      const response = await axios.post(`${API}/api/system/reset`, {
        password: password,
        confirmation_text: confirmationText
      });

      setResetResult(response.data);
      toast.success('Sistema reseteado exitosamente');
      
      // Clear form
      setConfirmationText('');
      setPassword('');
    } catch (error) {
      console.error('Error al resetear sistema:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear sistema');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header con advertencia */}
      <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="text-red-600 flex-shrink-0 mt-1" size={32} />
          <div>
            <h1 className="text-3xl font-bold text-red-800 mb-2">
              Reset del Sistema
            </h1>
            <p className="text-red-700 font-semibold">
              ⚠️ ATENCIÓN: Esta acción es IRREVERSIBLE y eliminará todos los datos transaccionales del sistema.
            </p>
            <p className="text-red-600 text-sm mt-1">
              Solo usuarios con rol SUPERADMIN pueden realizar esta operación.
            </p>
          </div>
        </div>
      </div>

      {/* Card principal */}
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-800 text-white px-6 py-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Trash2 size={24} />
            Datos que se eliminarán
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Lista de datos a eliminar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <Trash2 size={18} />
                Se eliminarán:
              </h3>
              <ul className="space-y-2 text-sm text-red-700">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Todas las facturas
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Clientes (excepto CONSUMIDOR FINAL)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Cuentas por cobrar
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Cuentas por pagar
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Movimientos de caja
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Todos los productos
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Todas las categorías
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Todos los proveedores
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Usuarios (excepto superadmin y admin)
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                <RefreshCw size={18} />
                Se mantendrán:
              </h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Usuario Superadmin
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Usuario Admin
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Cliente CONSUMIDOR FINAL
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Configuración del negocio
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Logo del comercio
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Rangos de NCF autorizados
                </li>
              </ul>
            </div>
          </div>

          {/* Formulario de confirmación */}
          <div className="border-t pt-6 space-y-4">
            <div>
              <Label htmlFor="confirmation" className="text-base font-semibold">
                1. Escribe exactamente: <span className="text-red-600">CONFIRMAR RESET</span>
              </Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Escribe: CONFIRMAR RESET"
                className={`mt-2 ${
                  confirmationText && confirmationText !== 'CONFIRMAR RESET'
                    ? 'border-red-500'
                    : ''
                }`}
                disabled={loading}
              />
              {confirmationText && confirmationText !== 'CONFIRMAR RESET' && (
                <p className="text-sm text-red-600 mt-1">
                  El texto no coincide. Debe ser exactamente: CONFIRMAR RESET
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-base font-semibold">
                2. Ingresa tu contraseña de Superadmin
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu contraseña"
                className="mt-2"
                disabled={loading}
              />
            </div>

            <Button
              onClick={handleReset}
              disabled={!isFormValid || loading}
              className={`w-full h-12 text-lg font-semibold ${
                isFormValid
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-slate-300 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <RefreshCw className="mr-2 animate-spin" size={20} />
                  Reseteando sistema...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2" size={20} />
                  RESETEAR SISTEMA
                </>
              )}
            </Button>
          </div>

          {/* Resultado del reset */}
          {resetResult && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3">
                ✅ Sistema reseteado exitosamente
              </h3>
              <div className="text-sm text-green-700 space-y-1">
                <p className="font-semibold">Datos eliminados:</p>
                <ul className="ml-4 space-y-1">
                  {Object.entries(resetResult.deleted || {}).map(([key, value]) => (
                    <li key={key}>
                      • {key}: {value} registro(s)
                    </li>
                  ))}
                </ul>
                <div className="mt-3 space-y-1">
                  <p className="font-semibold">
                    Usuarios restantes: {resetResult.remaining_users}
                  </p>
                  <p className="font-semibold">
                    Clientes restantes: {resetResult.remaining_clients || 1}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer info */}
      <div className="bg-slate-100 border border-slate-300 rounded-lg p-4 text-sm text-slate-600">
        <p className="font-semibold mb-2">ℹ️ Información:</p>
        <ul className="space-y-1 ml-4">
          <li>• Esta operación está diseñada para resetear el sistema en desarrollo o testing.</li>
          <li>• Los nuevos registros comenzarán con IDs automáticos generados por MongoDB.</li>
          <li>• Las cajas registradoras abiertas se cerrarán automáticamente.</li>
          <li>• La operación no tiene opción de deshacer.</li>
        </ul>
      </div>
    </div>
  );
};

export default SystemReset;
